/*
 * <m-product-discount-progress> — keeps the product page's discount ladder in
 * step with the cart.
 *
 * The cart drawer's copy of this ladder needs no JavaScript for its numbers: the
 * theme refetches the drawer from the server after every cart change and the
 * whole state is recomputed in Liquid. The product section is not refetched, so
 * this block updates itself.
 *
 * It never composes a sentence. Every message the block can show was rendered by
 * Liquid into the payload, keyed by poster count, so a translation is free to
 * introduce a plural form — "1 plakat" against "5 plakatów" — without this file
 * having to know the rule. The badge that carries the next percentage is in the
 * payload the same way, and for the same reason: the format is language. The
 * labels are the exception, and they are numbers rather than language.
 *
 * One segment is one poster, so the fill is the poster count itself: segment i
 * is on while the cart holds i posters and the next one is previewed. Nothing
 * here has to know where the thresholds fall to draw the track — the thresholds
 * decide only which percentage label is the one the customer is standing on.
 *
 * When the block is set to make the callout an add-to-cart button, this file also
 * owns two things Liquid cannot. It mirrors the main button's availability after
 * a variant change, because the block is not re-rendered with the section; and it
 * keeps the callout from firing a second request while the first is still in the
 * air, because the theme's own guard sits on the form and this button does not.
 */

(() => {
  if (customElements.get("m-product-discount-progress")) return;

  class MProductDiscountProgress extends HTMLElement {
    connectedCallback() {
      this.segmentEls = [...this.querySelectorAll("[data-segment]")];
      this.labelEls = [...this.querySelectorAll("[data-label-pct]")];
      this.messageEl = this.querySelector("[data-message]");
      this.liveEl = this.querySelector("[data-live]");
      this.badgeEl = this.querySelector("[data-badge]");
      this.calloutEl = this.querySelector(".m-discount-progress__reward--action");
      this.rewardEl = this.querySelector(".m-discount-progress__reward");
      this.rewardLabelEl = this.rewardEl
        ? this.rewardEl.querySelector(".m-discount-progress__reward-label")
        : null;
      this.updateToken = 0;

      this.topQty = parseInt(this.dataset.topQty, 10) || 0;
      this.posterType = this.dataset.posterType || "";
      this.hideWhenEmpty = this.dataset.hideWhenEmpty === "true";

      const payload = this.querySelector("[data-payload]");
      try {
        const parsed = payload ? JSON.parse(payload.textContent) : {};
        this.tiers = parsed.tiers || [];
        this.messages = parsed.messages || {};
        this.badges = parsed.badges || {};
      } catch (e) {
        console.error("m-product-discount-progress: bad payload", e);
        this.tiers = [];
        this.messages = {};
        this.badges = {};
      }

      if (!window.LeonartoEvents || !window.LeonartoTheme) return;
      const events = window.LeonartoTheme.pubSubEvents;
      this.unsubscribe = window.LeonartoEvents.subscribe(
        events.cartUpdate,
        this.onCartUpdate.bind(this)
      );
      this.unsubscribeVariant = window.LeonartoEvents.subscribe(
        events.variantChange,
        this.syncCalloutAvailability.bind(this)
      );
      this.unsubscribeError = window.LeonartoEvents.subscribe(
        events.cartError,
        this.releaseCallout.bind(this)
      );
      this.bindCallout();
      this.observeRewardWrap();
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      if (this.unsubscribeVariant) this.unsubscribeVariant();
      if (this.unsubscribeError) this.unsubscribeError();
      clearTimeout(this.pendingTimer);
      if (this.rewardResizeObserver) this.rewardResizeObserver.disconnect();
    }

    /* The callout is a submit button for a form that lives in another block, so
       none of the theme's own guards reach it: `m-spinner-loading` is set on the
       <product-form> element and disables `.m-spinner-button` inside it, while
       this button is a sibling of that block rather than a descendant.

       Availability first. The product section is not re-rendered on a variant
       change — product-info-bento.js swaps the variant picker and updates the
       form's own button — so a callout rendered as a button would stay one on a variant that
       cannot be bought. That button already carries `disabled` for exactly this
       state, which makes it the source of truth to copy, on every variant change
       and on first paint.

       Then re-entrancy. The vendor form had no guard of its own, so two clicks
       were two requests and two identical lines in the cart; <product-form>
       now drops a submit while one runs, and the callout still blocks
       itself on click and unblocks on the next cart event, success or error, with
       a timer as the floor for a request that never answers. */
    bindCallout() {
      if (!this.calloutEl) return;
      this.calloutEl.addEventListener("click", this.onCalloutClick.bind(this));
      this.syncCalloutAvailability();
    }

    getAtcButton() {
      return document.querySelector(".m-product-form--main [name='add']");
    }

    syncCalloutAvailability() {
      if (!this.calloutEl || this.pending) return;
      const atcButton = this.getAtcButton();
      /* No main button means no form to submit — the callout should not have been
         rendered as one, and blocking it is safer than letting it post. */
      this.calloutEl.setAttribute(
        "aria-disabled",
        String(!atcButton || atcButton.disabled)
      );
    }

    onCalloutClick(event) {
      if (this.calloutEl.getAttribute("aria-disabled") === "true") {
        event.preventDefault();
        return;
      }
      this.pending = true;
      this.calloutEl.setAttribute("aria-disabled", "true");
      clearTimeout(this.pendingTimer);
      this.pendingTimer = setTimeout(() => this.releaseCallout(), 10000);
    }

    releaseCallout() {
      this.pending = false;
      clearTimeout(this.pendingTimer);
      this.syncCalloutAvailability();
    }

    /* The separator sits between the label and the sentence while the two share a
       line, and has nothing left to separate once the sentence wraps below the
       label. Which of those two happens is decided by the column width, the font
       and the length of the translation — none of them visible to CSS — so the
       row is measured instead. Layout 1 has no reward row and the guards below
       are what keeps this a no-op there. */
    observeRewardWrap() {
      if (!this.rewardEl || !this.rewardLabelEl || !this.messageEl) return;
      this.syncRewardWrap();
      if (!window.ResizeObserver) return;
      this.rewardResizeObserver = new ResizeObserver(() => this.syncRewardWrap());
      this.rewardResizeObserver.observe(this.rewardEl);
    }

    syncRewardWrap() {
      if (!this.rewardEl || !this.rewardLabelEl || !this.messageEl) return;
      this.rewardEl.classList.toggle(
        "is-wrapped",
        this.messageEl.offsetTop > this.rewardLabelEl.offsetTop
      );
    }

    /* The payload's shape depends on which call produced it. Line edits in the
       drawer emit a whole cart; add-to-cart emits the /cart/add.js response,
       which is the added line item and carries no `items`. Only the first can
       be counted, so the second costs one request.

       An `items` array is NOT enough to call something a cart: a multi-line
       /cart/add.js response is `{items: [...just added...]}` too, and counting
       that would report the addition as if it were the whole basket. Every real
       cart response carries `item_count`; no add response does. */
    async onCartUpdate(payload) {
      /* A cart event means the request this button fired has been answered, one
         way or the other; the error path has its own subscription. */
      this.releaseCallout();

      const cart = payload && payload.cart;
      const isCart = cart && Array.isArray(cart.items) && typeof cart.item_count === "number";
      let items = isCart ? cart.items : null;

      /* Cart updates overlap: an add-to-cart sends us to the network while a
         quantity change in the drawer resolves synchronously right after. Only
         the newest one may paint, or the slower older request lands last and
         leaves the bar reading a cart that no longer exists. */
      const token = ++this.updateToken;

      if (!items) {
        try {
          const res = await fetch(`${window.LeonartoSettings.routes.cart}.js`, {
            headers: { "Content-Type": "application/json" },
          });
          items = (await res.json()).items || [];
        } catch (e) {
          console.error("m-product-discount-progress: could not read the cart", e);
          return;
        }
      }

      if (token !== this.updateToken) return;
      this.render(this.countPosters(items));
    }

    /* An empty poster type in the theme settings means "count every line",
       matching what the Liquid does on the first render. */
    countPosters(items) {
      return items.reduce((total, item) => {
        if (this.posterType && item.product_type !== this.posterType) return total;
        return total + (item.quantity || 0);
      }, 0);
    }

    /* The highest tier the count has already paid for, 0 before the first one.
       The same walk the Liquid does — last reached tier in sorted order — so the
       first render and every re-render agree on which label is current. */
    earnedPercent(posterCount) {
      return this.tiers.reduce((pct, [qty, tierPct]) => (posterCount >= qty ? tierPct : pct), 0);
    }

    render(posterCount) {
      if (this.hideWhenEmpty) this.hidden = posterCount === 0;
      if (!this.topQty) return;

      /* Past the last threshold there is no next reward to name, so layout 2's
         "NEXT REWARD" label steps aside and leaves the sentence that says the top
         was reached. Liquid sets the same class on the first render — a cart that
         is already past the top would otherwise carry the label until the next
         cart change. */
      this.classList.toggle("is-complete", posterCount >= this.topQty);

      /* One segment per poster: the fill is the count, and the segment after it
         is the poster the next click adds. Past the top tier no segment matches
         `posterCount + 1` and the track simply stays full. */
      this.segmentEls.forEach((el, i) => {
        const index = i + 1;
        el.classList.toggle("is-on", index <= posterCount);
        el.classList.toggle("is-next", index === posterCount + 1);
      });

      /* The label the customer is standing on. Liquid prints a label only where
         the discount rises, so no two carry the same number and this cannot
         light two at once. Nothing is lit at 0% — at that point the ladder has
         not been started and the sentence above says what the next poster is
         worth. */
      const earned = this.earnedPercent(posterCount);
      this.labelEls.forEach((el) => {
        el.classList.toggle("is-current", earned > 0 && parseInt(el.dataset.labelPct, 10) === earned);
      });

      const key = posterCount >= this.topQty ? "top" : String(posterCount);
      const message = this.messages[key];
      /* Leave the last correct sentence in place rather than blanking the line:
         a missing key means the ladder changed under a cached payload, and an
         empty live region would announce nothing at all. */
      if (message && message !== this.messageEl.textContent) {
        this.messageEl.textContent = message;
        /* The announcement lives on a separate element — see the snippet — so the
           two have to be written together. */
        if (this.liveEl) this.liveEl.textContent = message;
      }

      /* The badge is the only place the next percentage is printed now, so a
         stale one is a wrong number rather than a redundant one — it has to move
         with the count. The `top` state carries no badge (CSS hides it once the
         ladder is complete), so a missing key means there is nothing to set and
         the last correct value stays. */
      const badge = this.badges[key];
      if (badge && this.badgeEl && badge !== this.badgeEl.textContent) {
        this.badgeEl.textContent = badge;
      }

      /* The sentence just changed length, so the line break may have moved with
         it: re-measure whether the separator still has anything to separate. */
      this.syncRewardWrap();
    }
  }

  customElements.define("m-product-discount-progress", MProductDiscountProgress);
})();
